"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// Inline SVG perfume bottle — no external images needed
function BottleSVG({ withBackground }: { withBackground: boolean }) {
  return (
    <svg
      viewBox="0 0 200 340"
      xmlns="http://www.w3.org/2000/svg"
      className="h-full w-auto drop-shadow-xl"
      aria-hidden
    >
      {/* Studio gradient background (Before only) */}
      {withBackground && (
        <>
          <defs>
            <radialGradient id="studioBg" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="#7c3aed" />
              <stop offset="60%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1e1b4b" />
            </radialGradient>
            <radialGradient id="studioLight" cx="60%" cy="30%" r="50%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="200" height="340" fill="url(#studioBg)" />
          <rect width="200" height="340" fill="url(#studioLight)" />
          {/* Floor reflection */}
          <ellipse cx="100" cy="310" rx="55" ry="10" fill="#000" opacity="0.3" />
        </>
      )}

      {/* Bottle body defs (shared) */}
      <defs>
        <linearGradient id="bodyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#e8ddd0" />
          <stop offset="40%" stopColor="#f5f0e8" />
          <stop offset="100%" stopColor="#c8b99a" />
        </linearGradient>
        <linearGradient id="capGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#d4a96a" />
          <stop offset="100%" stopColor="#8b6340" />
        </linearGradient>
        <linearGradient id="neckGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#c8b99a" />
          <stop offset="50%" stopColor="#f5f0e8" />
          <stop offset="100%" stopColor="#c8b99a" />
        </linearGradient>
        <linearGradient id="labelGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2a1a0a" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#2a1a0a" stopOpacity="0.3" />
        </linearGradient>
        <filter id="innerShadow">
          <feOffset dx="4" dy="0" />
          <feGaussianBlur stdDeviation="3" />
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>
      </defs>

      {/* Cap */}
      <rect x="72" y="30" width="56" height="70" rx="8" fill="url(#capGrad)" />
      <rect x="76" y="34" width="20" height="62" rx="4" fill="#d4a96a" opacity="0.4" />
      {/* Cap top detail */}
      <rect x="78" y="28" width="44" height="8" rx="4" fill="#c8a882" />
      <rect x="82" y="30" width="14" height="4" rx="2" fill="#e8c090" opacity="0.5" />

      {/* Neck */}
      <rect x="80" y="96" width="40" height="30" rx="4" fill="url(#neckGrad)" />
      <rect x="84" y="98" width="12" height="26" rx="3" fill="#f5f0e8" opacity="0.3" />

      {/* Shoulder taper */}
      <path d="M60 126 Q60 136 70 138 L130 138 Q140 136 140 126 Z" fill="url(#bodyGrad)" />

      {/* Main bottle body */}
      <rect x="60" y="135" width="80" height="160" rx="10" fill="url(#bodyGrad)" />

      {/* Glass highlight — left edge */}
      <rect x="62" y="140" width="10" height="150" rx="5" fill="#ffffff" opacity="0.18" />

      {/* Glass highlight — center shimmer */}
      <rect x="94" y="145" width="5" height="140" rx="2.5" fill="#ffffff" opacity="0.08" />

      {/* Dark right edge (glass depth) */}
      <rect x="128" y="140" width="10" height="150" rx="5" fill="#8b7355" opacity="0.25" />

      {/* Label area */}
      <rect x="68" y="165" width="64" height="80" rx="4" fill="url(#labelGrad)" />
      <rect x="70" y="167" width="60" height="76" rx="3" fill="#1a0f05" opacity="0.15" />

      {/* Label text (decorative) */}
      <rect x="76" y="180" width="48" height="2" rx="1" fill="#f5f0e8" opacity="0.7" />
      <rect x="80" y="188" width="40" height="8" rx="2" fill="#f5f0e8" opacity="0.5" />
      <rect x="76" y="202" width="48" height="1.5" rx="0.75" fill="#f5f0e8" opacity="0.4" />
      <rect x="82" y="210" width="36" height="6" rx="2" fill="#f5f0e8" opacity="0.3" />
      <rect x="78" y="222" width="44" height="1.5" rx="0.75" fill="#f5f0e8" opacity="0.3" />
      <rect x="84" y="228" width="32" height="4" rx="2" fill="#f5f0e8" opacity="0.2" />

      {/* Bottom base */}
      <ellipse cx="100" cy="295" rx="40" ry="6" fill="#c8b99a" opacity="0.6" />
    </svg>
  );
}

export default function DemoSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderX, setSliderX] = useState(65); // 65% shows mostly "before"
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setSliderX(pct);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
      setHasInteracted(true);
      updateSlider(e.clientX);
    },
    [updateSlider]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      updateSlider(e.clientX);
    },
    [isDragging, updateSlider]
  );

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Hint animation — subtle pulse on the handle before first interaction
  useEffect(() => {
    if (hasInteracted) return;
    // nothing needed; CSS handles the pulse
  }, [hasInteracted]);

  return (
    <motion.section
      variants={fadeUp}
      className="mt-16 w-full"
      aria-label="Before and after background removal demo"
    >
      {/* Section label */}
      <div className="mb-8 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-1.5 text-xs font-medium text-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
          Live demo
        </span>
        <h2 className="mt-4 font-display text-2xl font-bold text-white sm:text-3xl">
          See it in action
        </h2>
        <p className="mt-2 text-sm text-muted">
          AI removes the background — keeping every detail sharp.
        </p>
      </div>

      {/* Comparison container */}
      <div
        ref={containerRef}
        className={`relative mx-auto aspect-[16/9] max-w-2xl overflow-hidden rounded-xl border border-border select-none ${isDragging ? "cursor-col-resize" : "cursor-col-resize"}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        {/* AFTER layer — checkerboard + bottle without bg */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <div className="checkerboard absolute inset-0 opacity-60" />
          <div className="relative z-10 flex h-full items-center justify-center p-8">
            <BottleSVG withBackground={false} />
          </div>
        </div>

        {/* BEFORE layer — gradient bg + bottle, clipped to show left portion */}
        <div
          className="absolute inset-0 flex items-center justify-center overflow-hidden"
          style={{
            clipPath: `inset(0 ${100 - sliderX}% 0 0)`,
          }}
        >
          <div className="absolute inset-0 bg-[#1e1b4b]" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 60% 30%, #7c3aed 0%, #3b82f6 50%, #1e1b4b 100%)",
            }}
          />
          {/* Floor shadow */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 w-32 h-4 rounded-full blur-md"
            style={{ background: "rgba(0,0,0,0.45)" }}
          />
          <div className="relative z-10 flex h-full items-center justify-center p-8">
            <BottleSVG withBackground={false} />
          </div>
        </div>

        {/* Slider divider line */}
        <div
          className="pointer-events-none absolute inset-y-0 z-20 w-0.5 -translate-x-1/2 bg-white/90"
          style={{ left: `${sliderX}%` }}
        />

        {/* Slider handle */}
        <div
          className="pointer-events-none absolute top-1/2 z-30 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${sliderX}%` }}
        >
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-accent shadow-lg transition-transform ${isDragging ? "scale-110" : hasInteracted ? "" : "animate-pulse"}`}
          >
            {/* Left/right arrows */}
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="black">
              <path d="M7 5l-5 5 5 5M13 5l5 5-5 5" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
        </div>

        {/* Before badge */}
        <div className="pointer-events-none absolute left-3 top-3 z-20 rounded-md bg-surface/80 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
          Before
        </div>

        {/* After badge */}
        <div className="pointer-events-none absolute right-3 top-3 z-20 rounded-md bg-accent/90 px-2.5 py-1 text-xs font-medium text-black backdrop-blur-sm">
          After
        </div>

        {/* Drag hint */}
        <motion.div
          className="pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1.5 text-xs text-white/80 backdrop-blur-sm"
          animate={{ opacity: hasInteracted ? 0 : 1 }}
          transition={{ duration: 0.4 }}
        >
          Drag to compare
        </motion.div>
      </div>
    </motion.section>
  );
}
